import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Container } from '@mui/material';
import ContentLoader from 'react-content-loader';
import Slider from 'react-slick';

import { breakpoints } from '~/src/styles';
import { paddingVariables, typography } from '~/src/styles/design-system';
import { fontWeight } from '~/src/styles/styled-variables';

import { ReactComponent as IconStar } from './star.svg';
import { CarouselItem } from './types';
import { WithPlaceholder } from '../../carousels';
import { Section, Wrapper } from '../../carousels/BaseCarousel/elements';

const cardDimension = {
  height: 460,
  width: 300,
};
export const spaceBetweenCards = 10;
const cardStyles = {
  container: css({
    width: `${cardDimension.width}px`,
    height: `${cardDimension.height}px`,
    paddingRight: `${spaceBetweenCards}px`,
    boxShadow: '0px 4px 10px 0px #00000014',
    border: '1px solid #E9E9E9',
    background: '#FFFFFF',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: paddingVariables.p24,
    [breakpoints.minTablet]: {
      gap: paddingVariables.p40,
      width: `auto`,
      height: `auto`,
      paddingRight: `0`,
      boxShadow: 'none',
      border: 'none',
      padding: '0px 56px',
    },
    [breakpoints.minDesktop]: {
      padding: '0px 80px',
    },
  }),
  rating: css({
    display: 'flex',
    gap: '10px',
    [breakpoints.minTablet]: {
      justifyContent: 'center',
    },
  }),
  text: css(typography.bodyText, {
    fontWeight: fontWeight.medium,
    color: 'var(--Grey-Text)',

    [breakpoints.minTablet]: css(typography.subHeading1, {
      color: 'var(--Grey-Text)',
      textAlign: `center`,
      fontWeight: fontWeight.medium,
    }),
  }),
  star: css({
    width: '25px',
    height: 'auto',
    color: 'var(--Primary)',
    [breakpoints.minTablet]: {
      width: '40px',
    },
  }),
  userDetails: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    [breakpoints.minTablet]: {
      textAlign: `center`,
    },
  }),
  name: css(typography.bodyText, {
    fontWeight: fontWeight.bold,
    color: 'var(--Dark-Text)',
    [breakpoints.minTablet]: {
      color: 'var(--Grey-Text)',
    },
    [breakpoints.minDesktop]: css(typography.subHeading1, {
      color: 'var(--Grey-Text)',
      fontWeight: fontWeight.bold,
      textAlign: `center`,
    }),
  }),
  role: css(typography.bodyText, {
    color: 'var(--Grey-Text)',
    [breakpoints.minDesktop]: css(typography.subHeading1, {
      color: 'var(--Grey-Text)',
      textAlign: `center`,
    }),
  }),
};

export const TestimonialCard: React.FC<{ item: CarouselItem }> & {
  dimension: { width: number; height: number };
} & WithPlaceholder = ({ item: { name, role, stars, text } }) => {
  const starsList = useMemo(() => {
    return (
      <>
        {Array(stars)
          .fill(null)
          .map((...params) => {
            const [, idx] = params;
            return <IconStar css={cardStyles.star} key={idx} />;
          })}
      </>
    );
  }, [stars]);

  return (
    <div css={cardStyles.container}>
      <div css={cardStyles.rating}>{starsList}</div>
      <div css={cardStyles.text}>{text}</div>
      <div css={cardStyles.userDetails}>
        <div css={cardStyles.name}>- {name}</div>
        <div css={cardStyles.role}>
          <i>{role}</i>
        </div>
      </div>
    </div>
  );
};
TestimonialCard.dimension = cardDimension;

const Placeholder: React.FC = () => {
  const placeholderSlickSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    rows: 1,
    slidesToShow: 5,
    slidesToScroll: 1,
    arrows: false,
    variableWidth: true,
    centerPadding: '0',
  };
  const cardCount = 2;
  const containerHeight = '456px';

  const renderCardPlaceholders = (Width: number, Height: number) => {
    const cardPlaceholders = [];
    for (let i = 0; i < cardCount; i++) {
      cardPlaceholders.push(
        <div key={i}>
          <ContentLoader
            style={{ height: Height, width: Width }}
            foregroundColor="#f3f3f3"
            backgroundColor="var(--Grey-Accent-Color)"
            height={Height}
            width={Width}
            speed={3}
            uniqueKey={`c.h${Height}.w${Width}`}
          >
            <rect x={0} y={0} rx={10} ry={10} height={Height} width={Width} />
          </ContentLoader>
        </div>,
      );
    }
    return cardPlaceholders;
  };

  return (
    <div
      style={{
        height: containerHeight,
        boxSizing: 'content-box',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      <Section>
        <Container maxWidth="xl">
          <Wrapper data-test-percy-hidden lineWidthPercentage={0}>
            <Slider {...placeholderSlickSettings}>
              {renderCardPlaceholders(cardDimension.width, cardDimension.height)}
            </Slider>
          </Wrapper>
        </Container>
      </Section>
    </div>
  );
};
TestimonialCard.Placeholder = Placeholder;
